import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Info } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import Icon from 'components/Icon/Icon';
import Tooltip from 'components/Tooltip/Tooltip';
import { Select } from 'components/Select/Select';
import { IOption } from 'components/Select/type';
import { IRootReducerState } from 'store/store';
import { useDebounce } from 'utils/hooks/useDebounce';
import { useIsQuotationMode } from 'utils/hooks/useIsQuotationMode';
import { useQuotationLineResolveParams } from 'utils/hooks/useQuotationLineResolveParams';
import { useGetTemplateOptions } from 'components/Modals/TemplatesModal/hooks';
import {
  setSelectedTemplate,
  setTemplateToApply,
} from 'store/SubmitConfiguration/actions/submitConfiguration';
import {
  resetManuallyChangedQuestionNames,
  resetManuallyClearedQuestionNames,
  resetTouchedQuestionNames,
  setAreAllFieldsTouched,
} from 'store/Form/actions/form';
import {
  Column,
  FormItemContainer,
  QuestionName,
  QuestionNameAsteriskInfoRow,
  TooltipWrapper,
} from '../QuestionRow/QuestionRow.styled';
import { TemplateRowContainer } from './TemplateSelectRow.styled';

export const TemplateSelectRow = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isQuotationMode = useIsQuotationMode();
  const isResolvingQuotationLine = !!useQuotationLineResolveParams();

  const modelId = useSelector((state: IRootReducerState) => state.modelInfo.id);
  const { templateToApply, selectedTemplate } = useSelector(
    (state: IRootReducerState) => state.submitConfigurationInfo
  );

  const [searchBy, setSearchBy] = useState<string>('');
  const debouncedSearchBy = useDebounce(searchBy);

  useEffect(() => {
    if (selectedTemplate) {
      dispatch(setSelectedTemplate(null));
    }
  }, [modelId]);

  const selectedOption: IOption | null = selectedTemplate
    ? {
        value: String(selectedTemplate.id),
        label: selectedTemplate.name,
        key: String(selectedTemplate.id),
      }
    : null;

  const isVisible = isQuotationMode && !isResolvingQuotationLine && !!modelId;

  const { templateOptions, fetchNextPage, hasNextPage } = useGetTemplateOptions(
    Number(modelId),
    debouncedSearchBy,
    isVisible
  );

  if (!isVisible) {
    return null;
  }

  return (
    <TemplateRowContainer showBorder={false} disabled={false}>
      <FormItemContainer hasValue={!!selectedOption} disabled={false}>
        <Column>
          <QuestionNameAsteriskInfoRow>
            <QuestionName>{t('Templates')}</QuestionName>
            <TooltipWrapper>
              <Tooltip
                content={t(
                  'Pick a saved template to fill in this configuration. It replaces the answers currently in the form, and you can keep editing afterwards.'
                )}
              >
                <Icon svg={Info} size={20} color={COLORS.GREEN} />
              </Tooltip>
            </TooltipWrapper>
          </QuestionNameAsteriskInfoRow>
        </Column>
        <Select
          name="template-select"
          width={'100%'}
          height={'40rem'}
          styles={{ width: '100%' }}
          placeholder={t('Select template')}
          options={templateOptions}
          controlledValue={selectedOption}
          translate={false}
          // Loading runs one query off the template's saved payload rather than
          // writing values field by field, which would fire the per-change query
          // loop mid-way through. Handled by the apply effect in QueryProvider.
          isDisabled={!!templateToApply}
          onChange={(option: IOption) => {
            dispatch(
              setSelectedTemplate({
                id: Number(option.value),
                name: option.label,
              })
            );
            // The applied answers arrive as query outputs, so anything the user
            // had marked as manually changed or cleared no longer describes the
            // form they are looking at.
            dispatch(resetTouchedQuestionNames());
            dispatch(resetManuallyChangedQuestionNames());
            dispatch(resetManuallyClearedQuestionNames());
            dispatch(setAreAllFieldsTouched(false));
            dispatch(setTemplateToApply(Number(option.value)));
          }}
          onInputChange={(value: string) => setSearchBy(value)}
          onMenuScrollToBottom={() => hasNextPage && fetchNextPage()}
        />
      </FormItemContainer>
    </TemplateRowContainer>
  );
};

export default TemplateSelectRow;
